import React, { useState, useEffect, useCallback } from 'react';
import { 
    Box, Card, CardHeader, Button, Typography, Table, TableBody, 
    TableCell, TableContainer, TableHead, TableRow, CircularProgress,
    Alert, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, 
    DialogActions, TextField, Chip
} from '@mui/material';
import { 
    Refresh as RefreshIcon, Check as ApproveIcon, Close as RejectIcon,
    AccessTime as TimeIcon
} from '@mui/icons-material';
import { getPendingRequests, approveRequest, rejectRequest, getPartners } from './api';

function formatDate(dateString) {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
}

function PendingRequestsPanel({ apiKey, onError }) {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [refreshKey, setRefreshKey] = useState(0);
    const [openApproveDialog, setOpenApproveDialog] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [clientName, setClientName] = useState('');
    const [actionInProgress, setActionInProgress] = useState(false);
    const [partners, setPartners] = useState({});
    
    // Fetch partners for name mapping
    const fetchPartners = useCallback(async () => {
        if (!apiKey) return;
        
        try {
            const data = await getPartners(apiKey);
            // Create a map of partner ID to partner name
            const partnerMap = {};
            (data || []).forEach(partner => {
                partnerMap[partner.id] = partner.name;
            });
            setPartners(partnerMap);
        } catch (err) {
            console.error("Failed to fetch partners:", err);
            // Don't show error to user, just log it
        }
    }, [apiKey]);
    
    // Fetch pending requests
    const fetchRequests = useCallback(async () => {
        if (!apiKey) return;
        
        setLoading(true);
        setError('');
        try {
            const data = await getPendingRequests(apiKey);
            // Sort by timestamp, newest first
            const sortedData = (data || []).sort((a, b) => 
                new Date(b.timestamp) - new Date(a.timestamp)
            );
            setRequests(sortedData);
            if(onError) onError(null);
        } catch (err) {
            console.error("Failed to fetch pending requests:", err);
            const errorMsg = err.data?.error || err.message || "Failed to fetch pending requests";
            setError(`Error: ${errorMsg} (Status: ${err.status || 'N/A'})`);
            if(onError) onError(errorMsg);
        } finally {
            setLoading(false);
        }
    }, [apiKey, onError]);
    
    useEffect(() => {
        fetchPartners();
        fetchRequests();
    }, [fetchPartners, fetchRequests, refreshKey]);
    
    // Handle refresh
    const handleRefresh = () => {
        setRefreshKey(prev => prev + 1);
    };
    
    // Open approval dialog
    const handleOpenApproveDialog = (request) => {
        setSelectedRequest(request);
        setClientName(request.name || '');
        setOpenApproveDialog(true);
    };
    
    // Handle request approval
    const handleApproveRequest = async () => {
        if (!selectedRequest) return;
        
        setActionInProgress(true);
        try {
            await approveRequest(apiKey, selectedRequest.requestID, clientName);
            setOpenApproveDialog(false);
            handleRefresh();
        } catch (err) {
            console.error(`Failed to approve request ${selectedRequest.requestID}:`, err);
            const errorMsg = err.data?.error || err.message || `Failed to approve request`;
            setError(`Error: ${errorMsg} (Status: ${err.status || 'N/A'})`);
        } finally {
            setActionInProgress(false);
        }
    };
    
    // Handle request rejection
    const handleRejectRequest = async (requestID) => {
        if (!window.confirm('Are you sure you want to reject this request?')) return;
        
        setActionInProgress(true);
        try {
            await rejectRequest(apiKey, requestID);
            handleRefresh();
        } catch (err) {
            console.error(`Failed to reject request ${requestID}:`, err);
            const errorMsg = err.data?.error || err.message || `Failed to reject request`;
            setError(`Error: ${errorMsg} (Status: ${err.status || 'N/A'})`);
        } finally {
            setActionInProgress(false);
        }
    };
    
    return (
        <Card elevation={2} sx={{ overflow: 'hidden', mt: 3 }}>
            <CardHeader
                title={<Typography variant="h6">Pending Requests</Typography>}
                action={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Chip 
                            label={`${requests.length} pending`} 
                            color={requests.length > 0 ? "warning" : "default"}
                            size="small"
                        />
                        <Button
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={handleRefresh}
                            disabled={loading}
                            size="small"
                        >
                            Refresh
                        </Button>
                    </Box>
                }
            />
            
            <Box sx={{ px: 2, pb: 2 }}>
                {loading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>}
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                
                {!loading && !error && (
                    <TableContainer sx={{ maxHeight: 400 }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Request ID</TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Group</TableCell>
                                    <TableCell>Source IP</TableCell>
                                    <TableCell>Timestamp</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {requests.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">
                                            <Typography color="textSecondary" sx={{ py: 2 }}>
                                                No pending requests
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    requests.map((request) => (
                                        <TableRow key={request.requestID}>
                                            <TableCell sx={{ fontFamily: 'monospace' }}>{request.requestID}</TableCell>
                                            <TableCell>{request.name || '(Unnamed)'}</TableCell>
                                            <TableCell>{partners[request.partnerId] || request.partnerId}</TableCell>
                                            <TableCell>{request.sourceIP}</TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <TimeIcon fontSize="small" color="action" />
                                                    {formatDate(request.timestamp)}
                                                </Box>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                                    <Tooltip title="Approve">
                                                        <IconButton 
                                                            size="small" 
                                                            color="success" 
                                                            onClick={() => handleOpenApproveDialog(request)}
                                                            disabled={actionInProgress}
                                                        >
                                                            <ApproveIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Reject">
                                                        <IconButton 
                                                            size="small" 
                                                            color="error" 
                                                            onClick={() => handleRejectRequest(request.requestID)}
                                                            disabled={actionInProgress}
                                                        >
                                                            <RejectIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Box>
            
            {/* Approve Dialog */}
            <Dialog open={openApproveDialog} onClose={() => !actionInProgress && setOpenApproveDialog(false)}>
                <DialogTitle>Approve Client Request</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Client Name"
                        fullWidth
                        value={clientName}
                        onChange={(e) => setClientName(e.target.value)}
                        disabled={actionInProgress}
                        helperText="You can modify the client name before approval"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenApproveDialog(false)} disabled={actionInProgress}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleApproveRequest} 
                        color="primary" 
                        disabled={actionInProgress}
                        variant="contained"
                    >
                        {actionInProgress ? <CircularProgress size={24} /> : 'Approve'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Card>
    );
}

export default PendingRequestsPanel;